import { Container, Eyebrow, Heading, Reveal, Section, Text, buttonClasses } from '@nexa/ui';
import heroBg1200 from '@/assets/hero-bg-1200.webp';
import heroBg1536 from '@/assets/hero-bg-1536.webp';
import heroBg768 from '@/assets/hero-bg-768.webp';
import { useContactDialog } from '@/core/contact';
import { useCopy } from '@/core/i18n';
import { HeroMockApp } from './HeroMockApp.tsx';

/**
 * `#top` — `template.html:605`.
 *
 * h1 và lead lấy từ `page.home.*` chứ không phải `hero.*`: hai chuỗi đó khớp từng chữ với thiết
 * kế và `types.ts` vốn đã ép mọi `PageId` phải có chúng. Một câu, một nguồn.
 *
 * KHÔNG bật `cv` (content-visibility): section này ở trên màn đầu và chứa phần tử LCP — bỏ qua
 * layout cho nó là tự làm chậm chính LCP (design doc §8.7).
 *
 * `tone="navy"` + gradient qua `className`: `Section` chỉ có 6 tone và không có `gradient-hero`.
 * Thêm tone thứ 7 là sửa `packages/ui` giữa lô (plan §0.2) ⇒ đi đường này. Nếu lô sau cũng phải
 * lặp lại thì đó mới là lúc đủ căn cứ để thêm tone.
 *
 * `py-0!` ghi đè nhịp dọc của `Section`: hero có padding riêng (96/32/0) vì nó KHÔNG có padding
 * dưới — mock app phải thò xuống section kế. `cn()` chỉ nối chuỗi (không phải tailwind-merge)
 * nên không có `!` thì thắng thua phụ thuộc thứ tự Tailwind xếp utility trong CSS.
 */
export function HeroSection() {
  const copy = useCopy();
  const openContact = useContactDialog();

  return (
    <Section
      id="top"
      tone="navy"
      labelledBy="hero-heading"
      className="relative overflow-hidden bg-[image:var(--gradient-hero)] py-0!"
    >
      {/*
        Ảnh nền: ứng viên LCP vì nó phủ toàn khung nhìn đầu ⇒ `eager` + `fetchPriority="high"`.
        `alt=""` vì nó thuần trang trí (opacity .32 + mix-blend-screen) — mô tả nó cho screen
        reader là thêm nhiễu chứ không thêm thông tin.
      */}
      <img
        src={heroBg1200}
        srcSet={`${heroBg768} 768w, ${heroBg1200} 1200w, ${heroBg1536} 1536w`}
        sizes="100vw"
        alt=""
        fetchPriority="high"
        loading="eager"
        decoding="async"
        className="absolute inset-0 size-full object-cover opacity-[var(--opacity-hero-image)] mix-blend-screen"
      />
      <div className="absolute inset-0 bg-[image:var(--gradient-hero-veil)]" />

      {/* Hero là 1 trong 2 section đặt gutter TRONG bề rộng tối đa — xem `Container`. */}
      <Container gutter="inside" className="relative pt-18 text-center md:pt-20 xl:pt-24">
        <Reveal className="inline-flex items-center gap-[9px] rounded-pill border border-[var(--on-dark-line-28)] bg-[var(--on-dark-fill-08)] px-4 py-[7px]">
          <span className="nx-pulse size-1.5 rounded-pill bg-accent" />
          <Eyebrow tone="white">{copy.hero.badge}</Eyebrow>
        </Reveal>

        <Reveal>
          <Heading
            level={1}
            size="hero"
            tone="on-dark"
            id="hero-heading"
            className="mx-auto mt-[26px] max-w-[16ch]"
          >
            {copy.page.home.heading}
          </Heading>
        </Reveal>

        <Reveal>
          <Text size="lead-lg" tone="on-dark" className="mx-auto mt-6 max-w-[60ch]">
            {copy.page.home.body}
          </Text>
        </Reveal>

        <Reveal className="mt-[34px] flex flex-col justify-center gap-3.5 sm:flex-row sm:flex-wrap">
          <button
            type="button"
            onClick={openContact}
            className={`${buttonClasses('inverse', 'lg')} w-full sm:w-auto`}
          >
            {copy.hero.ctaPrimary}
          </button>
          <button
            type="button"
            onClick={openContact}
            className={`${buttonClasses('on-dark', 'lg')} w-full sm:w-auto`}
          >
            {copy.hero.ctaSecondary}
          </button>
        </Reveal>

        <Reveal>
          <Text size="body-sm" tone="on-dark-soft" className="mt-5">
            {copy.hero.note}
          </Text>
        </Reveal>

        {/*
          Mock app thò xuống section kế: thiết kế `margin:52px auto -120px` cộng một spacer 150px
          bên dưới để phần thò ra không đè lên nội dung. Ở mobile khung thấp hơn nên cả hai số
          nhỏ lại theo (-64 / 88).
        */}
        <Reveal className="nx-rv-right mx-auto mt-13 -mb-16 max-w-[1120px] md:-mb-30">
          <HeroMockApp />
        </Reveal>
      </Container>

      <div className="h-22 md:h-[150px]" />
    </Section>
  );
}
